/**
 * Sync Manager Service
 * Handles automatic syncing of offline data when online
 * Shows progress and handles retries
 */

import api from '../utils/api';
import {
    initOfflineStorage,
    getPendingSyncItems,
    updateSyncItemStatus,
    clearSyncedItem,
    getOfflineStats,
    getAllFromStore,
    STORE_NAMES
} from './OfflineStorage';

// Sync state
let isSyncing = false;
let syncListeners = [];
let isOnline = navigator.onLine;

/**
 * Initialize sync manager - call on app start
 */
export const initSyncManager = async () => {
    await initOfflineStorage();

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial sync if online
    if (navigator.onLine) {
        setTimeout(() => syncAll(), 2000);
    }

};

/**
 * Handle going online
 */
const handleOnline = () => {
    isOnline = true;
    notifyListeners({ type: 'connection', isOnline: true });
    syncAll();
};

/**
 * Handle going offline
 */
const handleOffline = () => {
    isOnline = false;
    notifyListeners({ type: 'connection', isOnline: false });
};

/**
 * Sync all pending items
 */
export const syncAll = async () => {
    if (isSyncing) {
        return { success: false, reason: 'already_syncing' };
    }

    if (!navigator.onLine) {
        return { success: false, reason: 'offline' };
    }

    isSyncing = true;
    const results = { synced: 0, failed: 0, total: 0, items: [] };

    try {
        const pendingItems = await getPendingSyncItems();
        results.total = pendingItems.length;

        // Build detailed item list for UI
        const syncItems = pendingItems.map(item => ({
            id: item.id,
            type: item.type,
            clinic_name: item.data?.clinic_name || item.data?.clinicName || '',
            description: getSyncItemDescription(item),
            status: 'pending',
            error: null
        }));

        notifyListeners({ type: 'sync_start', items: syncItems, total: results.total });

        for (let i = 0; i < pendingItems.length; i++) {
            const item = pendingItems[i];
            const syncItem_data = syncItems[i];

            // Skip if too many retries
            if (item.retries >= 3) {
                syncItem_data.status = 'failed';
                syncItem_data.error = 'تجاوز الحد الأقصى من المحاولات';
                results.failed++;
                results.items.push(syncItem_data);
                notifyListeners({
                    type: 'sync_progress',
                    current: i + 1,
                    total: results.total,
                    item: syncItem_data,
                    items: syncItems
                });
                continue;
            }

            try {
                syncItem_data.status = 'syncing';
                await updateSyncItemStatus(item.id, 'syncing');
                notifyListeners({
                    type: 'sync_progress',
                    current: i + 1,
                    total: results.total,
                    item: syncItem_data,
                    items: syncItems
                });

                await syncItemAction(item);

                await clearSyncedItem(item.type, item.local_id);
                syncItem_data.status = 'done';
                results.synced++;

            } catch (error) {
                console.error('[SyncManager] Failed to sync item:', item.id, error);
                await updateSyncItemStatus(item.id, 'failed', error.message);
                syncItem_data.status = 'failed';
                syncItem_data.error = error.message;
                results.failed++;
            }
            results.items.push(syncItem_data);
        }

        notifyListeners({
            type: 'sync_complete',
            results,
            items: syncItems
        });

        // Broadcast global event so pages can auto-refresh
        window.dispatchEvent(new CustomEvent('pharmatrack:sync_complete', { detail: results }));

        return { success: true, results };
    } catch (error) {
        console.error('[SyncManager] Sync error:', error);
        notifyListeners({ type: 'sync_error', error: error.message });
        return { success: false, reason: 'error', error: error.message };
    } finally {
        isSyncing = false;
    }
};

/**
 * Get human-readable description for a sync item
 */
const getSyncItemDescription = (item) => {
    const typeLabels = { visit: 'زيارة', order: 'طلب', expense: 'مصروف' };
    const typeName = typeLabels[item.type] || item.type;
    const clinicName = item.data?.clinic_name || item.data?.clinicName || '';
    return clinicName ? `${typeName} - ${clinicName}` : typeName;
};

/**
 * Get pending items with detail (for SyncDetailPanel)
 */
export const getPendingSyncItemsDetailed = async () => {
    const items = await getPendingSyncItems();
    return items.map(item => ({
        id: item.id,
        type: item.type,
        clinic_name: item.data?.clinic_name || item.data?.clinicName || '',
        description: getSyncItemDescription(item),
        status: item.sync_status || 'pending',
        retries: item.retries || 0,
        created_at: item.created_at,
        error: null
    }));
};

/**
 * Sync a single item
 */
const syncItemAction = async (item) => {
    switch (item.type) {
        case 'visit':
            return syncVisit(item.data);
        case 'order':
            return syncOrder(item.data);
        case 'expense':
            return syncExpense(item.data);
        default:
            throw new Error(`Unknown sync type: ${item.type}`);
    }
};

/**
 * Sync a visit to server
 */
const syncVisit = async (visitData) => {
    // Remove offline-specific fields
    const { id, offline, sync_status, ...cleanData } = visitData;
    const response = await api.post('/visits', cleanData);
    return response.data;
};

/**
 * Sync an order to server
 */
const syncOrder = async (orderData) => {
    const { id, offline, sync_status, ...cleanData } = orderData;
    const response = await api.post('/orders', cleanData);
    return response.data;
};

/**
 * Sync an expense to server
 */
const syncExpense = async (expenseData) => {
    const { id, offline, sync_status, ...cleanData } = expenseData;
    const response = await api.post('/expenses', cleanData);
    return response.data;
};

/**
 * Add sync listener
 */
export const addSyncListener = (callback) => {
    syncListeners.push(callback);
    return () => {
        syncListeners = syncListeners.filter(cb => cb !== callback);
    };
};

/**
 * Notify all listeners
 */
const notifyListeners = (event) => {
    syncListeners.forEach(cb => {
        try {
            cb(event);
        } catch (e) {
            console.error('[SyncManager] Listener error:', e);
        }
    });
};

/**
 * Get current sync status
 */
export const getSyncStatus = async () => {
    const stats = await getOfflineStats();
    return {
        isOnline,
        isSyncing,
        ...stats
    };
};

/**
 * Check if we're online
 */
export const checkOnline = () => isOnline;

/**
 * Force offline check
 */
export const forceOfflineCheck = () => {
    isOnline = navigator.onLine;
    return isOnline;
};

/**
 * Cleanup on app close
 */
export const cleanupSyncManager = () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    syncListeners = [];
};

export default {
    initSyncManager,
    syncAll,
    getSyncStatus,
    addSyncListener,
    checkOnline,
    forceOfflineCheck,
    cleanupSyncManager,
    getPendingSyncItemsDetailed
};
